import dayjs, { Dayjs } from "dayjs"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

export enum IngestionDateFilter {
  ONE_MONTH = "ONE_MONTH",
  ONE_TO_THREE_MONTHS = "ONE_TO_THREE_MONTHS",
  THREE_TO_SIX_MONTHS = "THREE_TO_SIX_MONTHS",
  MORE_THAN_SIX_MONTHS = "MORE_THAN_SIX_MONTHS",
}

export type IngestionDateRange = {
  start: Dayjs | null
  end: Dayjs
}

export const useIngestionDateFilter = () => {
  const [ingestionDateFilter, setIngestionDateFilter] =
    useState<IngestionDateFilter | null>(null)

  const { t } = useTranslation("home")

  const options = useMemo(
    () => [
      {
        label: t("informationRequests.ingestionDateFilter.oneMonth"),
        value: IngestionDateFilter.ONE_MONTH,
      },
      {
        label: t("informationRequests.ingestionDateFilter.oneToThreeMonths"),
        value: IngestionDateFilter.ONE_TO_THREE_MONTHS,
      },
      {
        label: t("informationRequests.ingestionDateFilter.threeToSixMonths"),
        value: IngestionDateFilter.THREE_TO_SIX_MONTHS,
      },
      {
        label: t("informationRequests.ingestionDateFilter.moreThanSixMonths"),
        value: IngestionDateFilter.MORE_THAN_SIX_MONTHS,
      },
    ],
    [t]
  )

  const getIngestionDateRange = useCallback<
    () => IngestionDateRange | null
  >(() => {
    if (!ingestionDateFilter) {
      return null
    }

    const rangeResolver = {
      [IngestionDateFilter.ONE_MONTH]: () => {
        return {
          start: dayjs().subtract(1, "month"),
          end: dayjs(),
        }
      },
      [IngestionDateFilter.ONE_TO_THREE_MONTHS]: () => {
        return {
          start: dayjs().subtract(3, "month"),
          end: dayjs().subtract(1, "month").subtract(1, "day"),
        }
      },
      [IngestionDateFilter.THREE_TO_SIX_MONTHS]: () => {
        return {
          start: dayjs().subtract(6, "month").subtract(1, "day"),
          end: dayjs().subtract(3, "month").subtract(1, "day"),
        }
      },
      [IngestionDateFilter.MORE_THAN_SIX_MONTHS]: () => {
        return {
          start: null,
          end: dayjs().subtract(6, "month").subtract(1, "day"),
        }
      },
    }

    return rangeResolver[ingestionDateFilter]()
  }, [ingestionDateFilter])

  const satisfiesIngestionDateFilter = useCallback<
    (date: string | null) => boolean
  >(
    (date: string | null) => {
      const range = getIngestionDateRange()

      if (!range || !date || !dayjs(date).isValid()) {
        return true
      }

      const wrappedIngestionDate = dayjs(date)

      const isBeforeEnd = wrappedIngestionDate.isBefore(range.end)

      return range.start
        ? isBeforeEnd && wrappedIngestionDate.isAfter(range.start)
        : isBeforeEnd
    },
    [getIngestionDateRange]
  )

  return {
    options,
    ingestionDateFilter,
    setIngestionDateFilter,
    satisfiesIngestionDateFilter,
    getIngestionDateRange,
  }
}
